export const DESKTOP_VERSION = '0.3.33';
export const MOBILE_VERSION = '0.3.13';

export const GITHUB_DESKTOP_TAG = `desktop-v${DESKTOP_VERSION}`;
export const GITHUB_MOBILE_TAG = `mobile-v${MOBILE_VERSION}`;

const GITHUB_RELEASES_REPO = 'douglace/velko-pos-releases';
const GITHUB_DOWNLOAD = `https://github.com/${GITHUB_RELEASES_REPO}/releases/download`;

export const RELEASES_HOST = 'https://releases.velko-pos.com';
export const GITHUB_DESKTOP_BASE = `${GITHUB_DOWNLOAD}/${GITHUB_DESKTOP_TAG}`;
export const GITHUB_MOBILE_BASE = `${GITHUB_DOWNLOAD}/${GITHUB_MOBILE_TAG}`;
export const GITHUB_RELEASES_URL = `https://github.com/${GITHUB_RELEASES_REPO}/releases`;

export type DetectedOs = 'windows' | 'mac' | 'linux' | 'other';

export type DesktopPackage = {
  key: string;
  os: 'windows' | 'mac' | 'linux';
  osLabel: string;
  format: string;
  arch: string;
  file: string;
};

export const DESKTOP_PACKAGES: ReadonlyArray<DesktopPackage> = [
  {
    key: 'win-exe',
    os: 'windows',
    osLabel: 'Windows 10+',
    format: '.exe (installer)',
    arch: 'x64',
    file: `Velko.POS_${DESKTOP_VERSION}_x64-setup.exe`,
  },
  {
    key: 'win-msi',
    os: 'windows',
    osLabel: 'Windows 10+',
    format: '.msi (entreprise)',
    arch: 'x64',
    file: `Velko.POS_${DESKTOP_VERSION}_x64_en-US.msi`,
  },
  {
    key: 'mac-dmg-arm64',
    os: 'mac',
    osLabel: 'macOS 11+',
    format: '.dmg',
    arch: 'Apple Silicon (arm64)',
    file: `Velko.POS_${DESKTOP_VERSION}_aarch64.dmg`,
  },
  {
    key: 'mac-dmg-intel',
    os: 'mac',
    osLabel: 'macOS 11+',
    format: '.dmg',
    arch: 'Intel (x86_64)',
    file: `Velko.POS_${DESKTOP_VERSION}_x64.dmg`,
  },
  {
    key: 'linux-deb',
    os: 'linux',
    osLabel: 'Debian · Ubuntu',
    format: '.deb',
    arch: 'amd64',
    file: `Velko.POS_${DESKTOP_VERSION}_amd64.deb`,
  },
  {
    key: 'linux-rpm',
    os: 'linux',
    osLabel: 'Fedora · RHEL',
    format: '.rpm',
    arch: 'x86_64',
    file: `Velko.POS-${DESKTOP_VERSION}-1.x86_64.rpm`,
  },
  {
    key: 'linux-appimage',
    os: 'linux',
    osLabel: 'Linux (portable)',
    format: '.AppImage',
    arch: 'amd64',
    file: `Velko.POS_${DESKTOP_VERSION}_amd64.AppImage`,
  },
];

export const MOBILE_APK_FILE = 'app-release.apk';

export function detectOs(): DetectedOs {
  if (typeof navigator === 'undefined') return 'other';
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('mac')) return 'mac';
  if (ua.includes('win')) return 'windows';
  if (ua.includes('linux')) return 'linux';
  return 'other';
}

export function primaryPackageFor(os: DetectedOs): DesktopPackage {
  const defaultPackage = DESKTOP_PACKAGES[0] as DesktopPackage;
  const find = (kind: DesktopPackage['os']) =>
    DESKTOP_PACKAGES.find((p) => p.os === kind) ?? defaultPackage;
  switch (os) {
    case 'mac':
      return find('mac');
    case 'linux':
      return find('linux');
    case 'windows':
      return find('windows');
    default:
      return find('windows');
  }
}

export type MacArch = 'arm64' | 'x86_64' | 'unknown';

type NavigatorUAData = {
  getHighEntropyValues?: (hints: ReadonlyArray<string>) => Promise<{ architecture?: string }>;
};

export async function detectMacArch(): Promise<MacArch> {
  if (typeof navigator === 'undefined') return 'unknown';
  const uaData = (navigator as Navigator & { userAgentData?: NavigatorUAData }).userAgentData;
  if (!uaData?.getHighEntropyValues) return 'unknown';
  try {
    const data = await uaData.getHighEntropyValues(['architecture']);
    if (data.architecture === 'arm') return 'arm64';
    if (data.architecture === 'x86') return 'x86_64';
    return 'unknown';
  } catch {
    return 'unknown';
  }
}

export function macPackage(arch: Exclude<MacArch, 'unknown'>): DesktopPackage {
  const key = arch === 'arm64' ? 'mac-dmg-arm64' : 'mac-dmg-intel';
  const pkg = DESKTOP_PACKAGES.find((p) => p.key === key);
  if (!pkg) throw new Error(`mac package not found for key ${key}`);
  return pkg;
}

export function macPackages(): { arm64: DesktopPackage; intel: DesktopPackage } {
  return { arm64: macPackage('arm64'), intel: macPackage('x86_64') };
}

export function desktopPackageUrl(pkg: DesktopPackage): string {
  return `${GITHUB_DESKTOP_BASE}/${pkg.file}`;
}

export function mobileApkUrl(): string {
  return `${GITHUB_MOBILE_BASE}/${MOBILE_APK_FILE}`;
}

export const OS_LABEL: Record<DetectedOs, string> = {
  mac: 'macOS',
  windows: 'Windows',
  linux: 'Linux',
  other: 'Ton système',
};
